import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { useNavigate } from 'react-router-dom';
import { faCheck } from '@fortawesome/pro-light-svg-icons/faCheck';
import { faBan } from '@fortawesome/pro-light-svg-icons/faBan';
import { TypedMemo } from '../../../common/types';
import { SMLButton } from '../../../common/views/SMLButton/SMLButton';
import { useOutreachPlan } from '../hooks/useOutreachPlan';
import { useThreadSideActions } from '../hooks/useThreadSideActions';
import type { ReplyThread } from '../outreachTypes';
import { outreachCampaignTabPath } from '../outreachPaths';
import { shortDate } from '../outreachFormat';
import { threadHistory } from './threadHistory';
import { inboxMessages as messages } from './inboxMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for Outreach; each part uses its own slice of it */
import styles from './Inbox.module.scss';
/* eslint-enable css-modules/no-unused-class */

/**
 * The side panel of a thread (design outreach-v2 m1): about the person, the history, what Plug
 * watches, and the actions "She added it", "Open in the campaign" and "Never pitch again".
 */
const ThreadSideComponent: React.FC<{ accountId: number; thread: ReplyThread }> = ({ accountId, thread }) => {
  const intl = useIntl();
  const navigate = useNavigate();
  const { isDemo } = useOutreachPlan();
  const actions = useThreadSideActions(accountId, thread);
  const history = useMemo(
    () =>
      threadHistory(intl, thread).map((line) => (
        <div key={line.key}>
          <span>{shortDate(intl, line.at)}</span>
          {line.text}
        </div>
      )),
    [intl, thread]
  );
  const name = thread.contactName;
  const { targetUuid } = thread;
  return (
    <aside className={styles.side} data-testid="thread-side">
      <div>
        <div className={styles.sideKey}>{intl.formatMessage(messages.aboutTitle, { name })}</div>
        <div className={styles.sideValue}>
          {thread.targetName !== null && <b>{thread.targetName}</b>}
          <span>{thread.fromEmail}</span>
          <span>{intl.formatMessage(messages.aboutSong, { song: thread.songTitle })}</span>
        </div>
      </div>
      <div>
        <div className={styles.sideKey}>{intl.formatMessage(messages.historyTitle)}</div>
        <div className={styles.timeline}>{history}</div>
      </div>
      {thread.placement === null && thread.targetName !== null && (
        <div>
          <div className={styles.sideKey}>{intl.formatMessage(messages.watchTitle)}</div>
          <div className={styles.sideValue}>
            <span>{intl.formatMessage(messages.watchBody, { song: thread.songTitle })}</span>
          </div>
        </div>
      )}
      <div className={styles.sideActions}>
        {thread.placement === null && targetUuid !== null && (
          <SMLButton
            size="sm"
            outline
            omitCap
            leftIcon={faCheck}
            isLoading={actions.isMarkingAdded}
            disabled={isDemo}
            onClick={() => actions.markAdded(targetUuid)}
          >
            {intl.formatMessage(messages.addedIt)}
          </SMLButton>
        )}
        <SMLButton
          size="sm"
          outline
          omitCap
          onClick={() => {
            // eslint-disable-next-line no-void
            void navigate(outreachCampaignTabPath(thread.campaignUuid, 'people'));
          }}
        >
          {intl.formatMessage(messages.openCampaign)}
        </SMLButton>
        <SMLButton
          size="sm"
          outline
          omitCap
          leftIcon={faBan}
          isLoading={actions.isBlocking}
          disabled={isDemo}
          onClick={actions.neverPitch}
        >
          {intl.formatMessage(messages.neverPitch)}
        </SMLButton>
      </div>
    </aside>
  );
};

export const ThreadSide = TypedMemo(ThreadSideComponent);
